/**
 * Backend API Client
 * Connects frontend to CRM Backend
 */

const BACKEND_URL = process.env.NEXT_PUBLIC_API_URL || process.env.API_URL || 'http://localhost:3000'

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: any
  headers?: Record<string, string>
  tenantId?: string
  token?: string
}

export interface BackendResponse<T = any> {
  data?: T
  error?: string
  status: number
}

class BackendClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = BACKEND_URL
  }

  private getHeaders(options: RequestOptions): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    if (options.tenantId) {
      headers['X-Tenant-ID'] = options.tenantId
    }

    if (options.token) {
      headers['Authorization'] = `Bearer ${options.token}`
    }

    return headers
  }

  async request<T = any>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<BackendResponse<T>> {
    const url = `${this.baseUrl}/api/v1${endpoint}`
    const response = await fetch(url, {
      method: options.method || 'GET',
      headers: this.getHeaders(options),
      body: options.body ? JSON.stringify(options.body) : undefined,
    })

    const data = await response.json().catch(() => null)

    if (!response.ok) {
      // Backend errors may return { success: false, error: { code, message } }
      const errorMsg = typeof data?.error === 'string'
        ? data.error
        : data?.error?.message || data?.message || `Request failed with status ${response.status}`
      return {
        data,
        status: response.status,
        error: errorMsg,
      }
    }

    // Unwrap backend { success, data } envelope if present
    const unwrapped = data?.success && data?.data !== undefined ? data.data : data

    return {
      data: unwrapped,
      status: response.status,
    }
  }

  // Conversations
  async getConversations(tenantId: string, token?: string) {
    return this.request(`/conversations?tenantId=${tenantId}`, { tenantId, token })
  }

  async getConversation(id: string, tenantId: string, token?: string) {
    return this.request(`/conversations/${id}`, { tenantId, token })
  }

  // Messages
  async sendMessage(data: any, tenantId: string, token?: string) {
    return this.request('/messages', {
      method: 'POST',
      body: data,
      tenantId,
      token,
    })
  }

  // Templates
  async getTemplates(tenantId: string, token?: string) {
    return this.request(`/templates?tenantId=${tenantId}`, { tenantId, token })
  }

  async createTemplate(data: any, tenantId: string, token?: string) {
    return this.request('/templates', {
      method: 'POST',
      body: data,
      tenantId,
      token,
    })
  }

  // Analytics
  async getAnalytics(tenantId: string, params: any, token?: string) {
    const query = new URLSearchParams(params).toString()
    return this.request(`/analytics?tenantId=${tenantId}&${query}`, { tenantId, token })
  }

  // Permissions
  async getRoles(tenantId: string, token?: string) {
    return this.request(`/permissions/roles?tenantId=${tenantId}`, { tenantId, token })
  }

  async getUsers(tenantId: string, token?: string) {
    return this.request(`/users?tenantId=${tenantId}`, { tenantId, token })
  }

  // Health check
  async healthCheck() {
    return this.request('/health')
  }

  // --- Leads ---
  async getLeads(tenantId: string, filters?: Record<string, string>, token?: string) {
    const query = filters ? `&${new URLSearchParams(filters).toString()}` : ''
    return this.request(`/leads?tenantId=${tenantId}${query}`, { tenantId, token })
  }

  async createLead(data: any, tenantId: string, token?: string) {
    return this.request('/leads', { method: 'POST', body: data, tenantId, token })
  }

  async updateLead(id: string, data: any, tenantId: string, token?: string) {
    return this.request(`/leads/${id}`, { method: 'PUT', body: data, tenantId, token })
  }

  async deleteLead(id: string, tenantId: string, token?: string) {
    return this.request(`/leads/${id}`, { method: 'DELETE', tenantId, token })
  }

  async assignLead(id: string, agentId: string, tenantId: string, token?: string) {
    return this.request(`/leads/${id}/assign`, { method: 'PUT', body: { agentId }, tenantId, token })
  }

  async updateLeadStage(id: string, stage: string, tenantId: string, token?: string) {
    return this.request(`/leads/${id}/stage`, { method: 'PUT', body: { stage }, tenantId, token })
  }

  // --- Properties ---
  async getProperties(tenantId: string, filters?: Record<string, string>, token?: string) {
    const query = filters ? `&${new URLSearchParams(filters).toString()}` : ''
    return this.request(`/properties?tenantId=${tenantId}${query}`, { tenantId, token })
  }

  async createProperty(data: any, tenantId: string, token?: string) {
    return this.request('/properties', { method: 'POST', body: data, tenantId, token })
  }

  async updateProperty(id: string, data: any, tenantId: string, token?: string) {
    return this.request(`/properties/${id}`, { method: 'PUT', body: data, tenantId, token })
  }

  async deleteProperty(id: string, tenantId: string, token?: string) {
    return this.request(`/properties/${id}`, { method: 'DELETE', tenantId, token })
  }

  // --- Visits ---
  async getVisits(tenantId: string, filters?: Record<string, string>, token?: string) {
    const query = filters ? `&${new URLSearchParams(filters).toString()}` : ''
    return this.request(`/visits?tenantId=${tenantId}${query}`, { tenantId, token })
  }

  async createVisit(data: any, tenantId: string, token?: string) {
    return this.request('/visits', { method: 'POST', body: data, tenantId, token })
  }

  async updateVisit(id: string, data: any, tenantId: string, token?: string) {
    return this.request(`/visits/${id}`, { method: 'PUT', body: data, tenantId, token })
  }

  async deleteVisit(id: string, tenantId: string, token?: string) {
    return this.request(`/visits/${id}`, { method: 'DELETE', tenantId, token })
  }

  // --- AI ---
  async getInsights(tenantId: string, token?: string) {
    return this.request(`/ai/insights?tenantId=${tenantId}`, { tenantId, token })
  }

  async sendAIChat(message: string, tenantId: string, token?: string) {
    return this.request('/ai/chat', { method: 'POST', body: { message }, tenantId, token })
  }

  async getPrequalificationLeads(tenantId: string, token?: string) {
    return this.request(`/ai/prequalification?tenantId=${tenantId}`, { tenantId, token })
  }

  async savePrequalification(leadId: string, data: any, tenantId: string, token?: string) {
    return this.request('/ai/prequalification', { method: 'POST', body: { leadId, ...data }, tenantId, token })
  }

  async getRecommendations(leadId: string, tenantId: string, token?: string) {
    return this.request(`/ai/recommendations?leadId=${leadId}&tenantId=${tenantId}`, { tenantId, token })
  }

  // --- Widget ---
  async getWidgetConfig(tenantId: string, token?: string) {
    return this.request(`/widget/config?tenantId=${tenantId}`, { tenantId, token })
  }

  async updateWidgetConfig(data: any, tenantId: string, token?: string) {
    return this.request('/widget/config', { method: 'PUT', body: data, tenantId, token })
  }

  async sendWidgetMessage(data: any, tenantId: string, token?: string) {
    return this.request('/widget/message', { method: 'POST', body: data, tenantId, token })
  }

  async getWidgetStats(tenantId: string, token?: string) {
    return this.request(`/widget/stats?tenantId=${tenantId}`, { tenantId, token })
  }

  // --- Tenants ---
  async deleteTenant(id: string, token?: string) {
    return this.request(`/tenants/${id}`, { method: 'DELETE', token })
  }
}

export const backendClient = new BackendClient()
export default backendClient
